import React from 'react'
import { motion } from 'framer-motion'
import { XIcon } from 'lucide-react'
import { BadgeDefinition } from '../types/badge'

interface BadgeModalProps {
  badge: BadgeDefinition
  onClose: () => void
}

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
}

const panelVariants = {
  hidden: { opacity: 0, scale: 0.88, y: 12 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.92, y: 8 },
}

export function BadgeModal({ badge, onClose }: BadgeModalProps) {
  const titleId = `badge-title-${badge.id}`

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      variants={backdropVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      transition={{ duration: 0.2 }}
    >
      <motion.div
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      <motion.div
        role="dialog"
        aria-labelledby={titleId}
        aria-modal="true"
        className="relative z-10 w-full max-w-md max-h-[90vh] overflow-y-auto bg-[#0d0d0d]/95 backdrop-blur-2xl border border-white/15 rounded-3xl px-6 py-6 shadow-2xl"
        variants={panelVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <XIcon className="w-4 h-4 text-white/50" />
        </button>

        <motion.div className="flex flex-col items-center text-center">
          <motion.img
            src={badge.imageSrc}
            alt={badge.imageAlt}
            className="w-full max-w-[220px] md:max-w-[260px] h-auto drop-shadow-2xl"
            initial={{ scale: 0.85, rotate: -4 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
          />

          <p className="mt-4 text-[10px] tracking-[0.25em] uppercase text-white/40">
            {badge.eyebrow}
          </p>
          <h2
            id={titleId}
            className="mt-1 text-xl md:text-2xl tracking-widest uppercase text-white"
          >
            {badge.title}
          </h2>
          {badge.subtitle && (
            <p className="mt-1 text-xs text-amber-200/80 tracking-widest uppercase">
              {badge.subtitle}
            </p>
          )}
        </motion.div>

        <div className="mt-6 space-y-4 text-sm leading-relaxed text-white/75">
          {badge.lead && (
            <p className="uppercase tracking-wide text-xs text-white/90">
              {badge.lead}
            </p>
          )}
          {badge.body.map((paragraph) => (
            <p key={paragraph.slice(0, 32)}>{paragraph}</p>
          ))}
          {badge.footnote && (
            <p className="text-white/55 text-xs italic">{badge.footnote}</p>
          )}
        </div>

        {badge.statusEffects.length > 0 && (
          <motion.div
            className="mt-6 pt-5 border-t border-white/10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            <p className="text-[10px] tracking-widest uppercase text-white/40 mb-3 text-center">
              Active status effects
            </p>
            <motion.div className="flex flex-wrap justify-center gap-2">
              {badge.statusEffects.map((fx, i) => (
                <motion.span
                  key={fx.label}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i }}
                  className={`px-2.5 py-1 rounded-full bg-white/8 border border-white/15 text-[10px] tracking-wider uppercase ${fx.tone}`}
                >
                  {fx.label}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>
        )}

        <p className="mt-6 text-center text-[10px] text-white/30 tracking-widest uppercase">
          Tap outside to seal dossier
        </p>
      </motion.div>
    </motion.div>
  )
}
